<template>
    <VJoyPanel data-testid="project-detail-stats">
        <template #panel-body>
            <div class="project-details-stats / flex">
                <div v-for="item of projectStatsItems" :key="item.icon" class="project-details-stats_item / flex">
                    <div v-if="item.id === 'daily-rate' || item.id === 'total-price'" class="project-details-stats_item_label">
                        <div class="project-details-stats_item_label_tooltip">{{ item.label }}</div>
                        <VJoyTooltip variant="secondary">
                            <VJoyIcon name="question-circle" />
                            <template #tooltip-content>
                                {{
                                    item.id === 'daily-rate'
                                        ? t('projectclient.projectview.details.projectinformations.daily-rate.tooltip')
                                        : t('projectclient.projectview.details.projectinformations.total-price.tooltip')
                                }}
                            </template>
                        </VJoyTooltip>
                    </div>
                    <span v-else class="project-details-stats_item_label">
                        {{ item.label }}
                    </span>
                    <div class="project-details-stats_item_content / flex">
                        <VJoyIcon color="neutral" :name="item.icon" />
                        <span class="project-details-stats_item_content_value" :data-testid="`project-detail-stats-${item.id}`">
                            {{ item.value }}
                        </span>
                    </div>
                </div>
            </div>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {formatDuration} from 'date-fns';
    import * as locales from 'date-fns/locale';
    import {storeToRefs} from 'pinia';
    import {parse} from 'tinyduration';
    import {useFormat, useTranslation, useLocale, useLogger} from '#imports';
    import {VJoyIcon, VJoyPanel, VJoyTooltip} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import {useProjectInformationStore} from '@/common/stores';

    type ProjectStat = {label: string; id: string; icon: JoyIconsId; value: any; condition?: boolean};

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const {t} = useTranslation();
    const {languageCode} = useLocale();
    const logger = useLogger();

    const {formatDate, formatAmount} = useFormat();

    const projectDuration = computed(() => {
        if (projectInformation.value.projectDuration) {
            try {
                const duration = parse(projectInformation.value.projectDuration);
                return formatDuration(duration, {locale: (locales as any)[languageCode.value]});
            } catch (e) {
                logger.error(e);
                return '-';
            }
        }
        return '-';
    });

    const projectFrequency = computed(() => {
        if (projectInformation.value.freelancerWorkedFrequency && projectInformation.value.freelancerWorkedUnit) {
            if (projectInformation.value.freelancerWorkedUnit === 'DAY') {
                return t('projectclient.projectview.details.projectinformations.frequency.value.days', {
                    unit: projectInformation.value.freelancerWorkedFrequency,
                });
            } else {
                return t('projectclient.projectview.details.projectinformations.frequency.value.hours', {
                    unit: projectInformation.value.freelancerWorkedFrequency,
                });
            }
        }
        return '-';
    });

    const estimatedTotalWorkAmount = computed(() => {
        if (projectInformation.value.freelancerEstimatedTotalWorkedAmount && projectInformation.value.freelancerWorkedUnit) {
            if (projectInformation.value.freelancerWorkedUnit === 'DAY') {
                return t('projectclient.projectview.details.projectinformations.total-work-amount.value.days', {
                    unit: projectInformation.value.freelancerEstimatedTotalWorkedAmount,
                });
            } else {
                return t('projectclient.projectview.details.projectinformations.total-work-amount.value.hours', {
                    unit: projectInformation.value.freelancerEstimatedTotalWorkedAmount,
                });
            }
        }
        return null;
    });

    const freelancerWorkedRate = computed(() => {
        if (projectInformation.value.freelancerWorkedRate) {
            return `${formatAmount(projectInformation.value.contract.currency, projectInformation.value.freelancerWorkedRate, {decimals: 2})}`;
        }
        return '-';
    });

    const projectStatsItems = computed(() => {
        const stats: ProjectStat[] = [
            {
                id: 'start-date',
                label: t('projectclient.projectview.details.projectinformations.start-date.label'),
                icon: 'flag',
                value: formatDate(projectInformation.value.contract.startDate, {day: '2-digit', month: '2-digit', year: 'numeric'}),
            },
            {
                id: 'end-date',
                label: t('projectclient.projectview.details.projectinformations.end-date.label'),
                icon: 'flag',
                value: formatDate(projectInformation.value.contract.endDate, {day: '2-digit', month: '2-digit', year: 'numeric'}),
                condition: projectInformation.value.contract.endDate != null,
            },
            {
                id: 'contract-type',
                label: t('projectclient.projectview.details.projectinformations.contract-type.label'),
                icon: 'copy',
                value: t(`projectclient.projectview.details.projectinformations.contract-type.value.${projectInformation.value.contract.type}`),
            },
            {
                id: 'project-duration',
                label: t('projectclient.projectview.details.projectinformations.project-duration.label'),
                icon: 'clock',
                value: projectDuration.value,
                condition: projectInformation.value.contract.type === 'COST_PLUS' && projectInformation.value.contract.endDate == null,
            },
            {
                id: 'frequency',
                label: t('projectclient.projectview.details.projectinformations.frequency.label'),
                icon: 'calendar',
                value: projectFrequency.value,
                condition: projectInformation.value.contract.type === 'COST_PLUS' && estimatedTotalWorkAmount.value == null,
            },
            {
                id: 'total-work-amount',
                label: t('projectclient.projectview.details.projectinformations.total-work-amount.label'),
                icon: 'calendar',
                value: estimatedTotalWorkAmount.value,
                condition: projectInformation.value.contract.type === 'COST_PLUS' && estimatedTotalWorkAmount.value != null,
            },
            {
                id: `${projectInformation.value.freelancerWorkedUnit === 'DAY' ? 'daily' : 'hourly'}-rate`,
                label: t(
                    `projectclient.projectview.details.projectinformations.${
                        projectInformation.value.freelancerWorkedUnit === 'DAY' ? 'daily' : 'hourly'
                    }-rate.label`,
                ),
                icon: 'cost-plus',
                value: freelancerWorkedRate.value,
                condition: projectInformation.value.contract.type === 'COST_PLUS',
            },
            {
                id: 'total-price',
                label: t('projectclient.projectview.details.projectinformations.total-price.label'),
                icon: 'cost-plus',
                value: `${formatAmount(projectInformation.value.contract.currency, projectInformation.value.lot.invoiceDetails.subTotalOutOfTaxes, {
                    decimals: 2,
                })}`,
                condition: projectInformation.value.contract.type === 'FIXED_PRICE',
            },
        ];
        return stats.filter(({condition}) => condition || condition == null);
    });
</script>

<style lang="scss" scoped>
    .project-details-stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(118px, 116px));
        justify-content: flex-start;
        gap: var(--joy-core-spacing-6) var(--joy-core-spacing-2);

        &_item {
            flex-flow: column nowrap;
            align-items: flex-start;
            row-gap: var(--joy-core-spacing-2);

            &_label {
                font-size: var(--joy-font-size-primary-200);
                display: flex;

                &_tooltip {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                :deep(.joy-icon) {
                    font-size: var(--joy-font-size-primary-300) !important;
                    color: var(--joy-color-text-default);

                    i {
                        font-size: var(--joy-font-size-primary-300) !important;
                    }
                }
            }

            &_content {
                column-gap: var(--joy-core-spacing-2);

                &_value {
                    font-weight: var(--joy-font-weight-bold);
                    font-size: var(--joy-font-size-primary-300);
                }
            }
        }
    }
</style>
